import { useEffect, useState } from "react";
import { IApartmentModalProps } from "../types/modal";

import { useAppDispatch } from "../hooks/redux";
import { closeModalById, openModal } from "../features/modals";

import { useFormik } from "formik";
import axios from "axios";

import {
  Dialog,
  DialogActions,
  DialogTitle,
  Button,
  DialogContent,
  TextField,
  Stack,
  Autocomplete,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

import apartmentSchema, { ApartmentBase } from "../schemas/apartment";
import PermissionGuard from "../components/PermissionGuard";
import PERMISSIONS from "../constants/permissions";
import MODALS from "../constants/modals";
import { ClinicResponse } from "../types/clinic";

const ApartmentModal: React.FunctionComponent<IApartmentModalProps> = ({
  id,
  callback,
  initialValues,
}) => {
  const dispatch = useAppDispatch();

  const [clinics, setClinics] = useState<ClinicResponse[]>([]);

  const [isDeleting, setIsDeleting] = useState(false);

  const {
    values,
    dirty,
    handleChange,
    setFieldValue,
    handleSubmit,
    errors,
    resetForm,
    submitCount,
  } = useFormik<ApartmentBase>({
    initialValues: {
      name: initialValues ? initialValues.name : "",
      clinic: initialValues ? initialValues.clinic._id : "",
    },
    onSubmit: (values) => {
      axios
        .put(
          "/classifiers/apartments",
          initialValues ? { _id: initialValues._id, ...values } : values
        )
        .then(() => {
          callback();
          handleClose();
        })
        .catch((err) => {
          console.log("err:", err);
        });
    },
    validationSchema: apartmentSchema,
  });

  const handleDelete = (id: string) => {
    setIsDeleting(true);
    axios
      .delete(`/classifiers/apartments/${id}`)
      .then(() => {
        callback();
        handleClose();
      })
      .catch((err) => {
        console.log("err:", err);
        setIsDeleting(false);
      });
  };

  const handleClose = () => {
    dispatch(closeModalById(id));
  };

  useEffect(() => {
    axios
      .get<ClinicResponse[]>("/clinics")
      .then((res) => {
        setClinics(res.data);
      })
      .catch((err) => {
        console.log("err:", err);
      });
  }, []);

  return (
    <Dialog fullWidth={true} maxWidth="xs" open={true} onClose={handleClose}>
      <DialogTitle>{"_id" in values ? "Labot" : "Pievienot"} dienesta dzīvokli</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <TextField
            id="name"
            label="Nosaukums"
            variant="standard"
            value={values.name}
            onChange={handleChange}
            error={Boolean(errors.name) && submitCount > 0}
          />
          <Autocomplete
            options={clinics}
            getOptionLabel={(option) => option.name}
            value={clinics.find((clinic) => values.clinic === clinic._id) || null}
            onChange={(event: any, newValue: ClinicResponse | null) => {
              setFieldValue("clinic", newValue ? newValue._id : null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                label="Klīnika"
                error={Boolean(errors.clinic) && submitCount > 0}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <PermissionGuard permission={PERMISSIONS.DELETE_APARTMENTS}>
          {({ hasAccess }) => (
            <>
              {hasAccess && initialValues ? (
                <LoadingButton
                  loading={isDeleting}
                  onClick={() => {
                    setIsDeleting(true);
                    dispatch(
                      openModal({
                        type: MODALS.Approve,
                        title: "Vai tiešām dzēst dienesta dzīvokli?",
                        message: "Dienesta dzīvoklis tiks dzēsts neatgriezeniski.",
                        onApprove: () => {
                          handleDelete(initialValues._id);
                        },
                        onReject: () => {
                          setIsDeleting(false);
                        },
                      })
                    );
                  }}
                  color="error"
                  variant="outlined"
                >
                  Dzēst
                </LoadingButton>
              ) : null}
            </>
          )}
        </PermissionGuard>
        <Stack direction="row" spacing={2} justifyContent="flex-end" flex={1}>
          <Button onClick={handleClose}>Aizvērt</Button>
          <Button
            disabled={isDeleting}
            onClick={() => {
              handleSubmit();
            }}
            variant="contained"
          >
            {initialValues ? "Saglabāt" : "Pievienot"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default ApartmentModal;
